import React, { useState } from 'react';
import { Share2, Copy, Mail, Check, Link2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';

export const ShareScenarioDialog = ({ 
  scenario,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');

  const shareUrl = scenario?.share_token 
    ? `${window.location.origin}/scenarios/${scenario.share_token}`
    : '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Share link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleEmailShare = () => {
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    const roi = scenario.results?.roi_percent || scenario.results?.total_roi_percent || 0;
    const financialImpact = Math.round(scenario.results?.financial_impact || scenario.results?.total_financial_impact || 0);
    
    const subject = encodeURIComponent(`LEI ROI Analysis: ${scenario.name}`);
    const body = encodeURIComponent(
`Hi,

I'd like to share this Seertech analysis with you:

${scenario.name}
${scenario.description || ''}

Key Results:
• ROI: ${(roi / 100).toFixed(1)}x return
• Financial Impact: $${financialImpact.toLocaleString()}
• LEI Improvement: ${scenario.configuration?.lei_improvement || 0}%

View the full analysis here:
${shareUrl}

This link will allow you to load the complete analysis with all configuration details and results.

Best regards`
    );

    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
    toast.success('Email client opened');
    setEmail('');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `LEI Analysis: ${scenario.name}`,
          text: `Check out this Seertech analysis with ${(scenario.results?.roi_percent / 100 || 0).toFixed(1)}x ROI`,
          url: shareUrl,
        });
        toast.success('Shared successfully');
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      handleCopyLink();
    }
  };

  if (!scenario) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" data-testid="share-scenario-btn">
            <Share2 className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-teal-600" />
            Share Scenario
          </DialogTitle>
          <DialogDescription>
            Share "{scenario.name}" with colleagues via link or email.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Share Link Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Share Link</Label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={shareUrl}
                  readOnly
                  className="pl-9 pr-2 text-sm bg-slate-50 text-slate-600"
                  data-testid="share-url-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className={copied ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : ''}
                data-testid="copy-link-btn"
              >
                {copied ? (
                  <>
                    <Check className="mr-1 h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="mr-1 h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Anyone with this link can view and load this analysis.
            </p>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-400">or share via</span>
            </div>
          </div>

          {/* Email Share Section */}
          <div className="space-y-3">
            <Label htmlFor="share-email" className="text-sm font-medium">Email</Label>
            <div className="flex gap-2">
              <Input
                id="share-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="flex-1"
                data-testid="share-email-input"
              />
              <Button
                onClick={handleEmailShare}
                className="bg-navy-700 hover:bg-navy-800"
                data-testid="email-share-btn"
              >
                <Mail className="mr-1 h-4 w-4" />
                Send
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Opens your email client with a pre-filled message containing the analysis summary.
            </p>
          </div>

          {/* Quick Share Button (for mobile/native share) */}
          {typeof navigator.share === 'function' && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleNativeShare}
              data-testid="native-share-btn"
            >
              <Share2 className="mr-2 h-4 w-4" />
              Share via Apps
            </Button>
          )}

          {/* Scenario Summary */}
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <p className="font-medium text-slate-700 mb-2">What they'll see:</p>
            <ul className="text-xs text-slate-600 space-y-1">
              <li>• Complete analysis configuration and inputs</li>
              <li>• All calculated results and ROI metrics</li>
              <li>• Monte Carlo simulation data</li>
              <li>• Ability to modify and re-run the analysis</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareScenarioDialog;
